/**
 * Public Layout
 * ============================================================================
 *
 * Purpose:
 * --------
 * Layout wrapper for public website pages (home, about, contact).
 * Provides header, footer, and main content area for marketing pages.
 *
 * Design Principles:
 * ------------------
 * - Marketing-focused: Designed for public-facing content
 * - Navigation: Public header with navigation links
 * - Footer: Company info and links
 * - Responsive: Mobile-first responsive design
 * ============================================================================
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS } from 'navigation/constants';

interface PublicLayoutProps {
  children: ReactNode;
}

const PublicLayout = ({ children }: PublicLayoutProps): JSX.Element => {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to={ROUTE_PATHS.HOME} className="flex items-center">
              <span className="text-2xl font-bold text-blue-600">
                Bimanova
              </span>
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex space-x-8">
              <Link
                to={ROUTE_PATHS.HOME}
                className="text-gray-700 hover:text-blue-600 transition-colors"
              >
                Home
              </Link>
              <Link
                to={ROUTE_PATHS.LOGIN}
                className="text-gray-700 hover:text-blue-600 transition-colors"
              >
                Login
              </Link>
              <Link
                to={ROUTE_PATHS.REGISTER}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Get Started
              </Link>
            </nav>

            {/* Mobile menu button */}
            <button className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100">
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Company Info */}
            <div>
              <h3 className="text-xl font-bold mb-4">Bimanova</h3>
              <p className="text-gray-400">
                Modern insurance management platform for East Africa.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to={ROUTE_PATHS.HOME}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Home
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.LOGIN}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Login
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.REGISTER}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Register
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <p className="text-gray-400">
                Email: info@bimanova.com
                <br />
                Tanzania
              </p>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>
              &copy; {new Date().getFullYear()} Bimanova. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

PublicLayout.displayName = 'PublicLayout';

export default PublicLayout;
